import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import { useRouter } from 'next/router';

import { ProductSelectModalView } from 'components/MultiProductDateForm/ProductSelectModalView';
import { ProductGroupDatePicker } from 'components/MultiProductDateForm/ProductGroupDatePicker';
import { ReduxState } from 'ducks';
import { useBounds } from 'hooks/useBounds';
import { BookingWidgetCalendarProductGroup } from 'models/settings';

import { RequestIcon } from './RequestIcon';
import { InstantIcon } from './InstantIcon';
import { useUrl } from 'hooks/useUrl';

import styles from './MultiProductDateForm.module.css';
import {
  attrMapHasFullyBookedDate,
  attrMapHasPromotionDate,
  getAttrByDateMap,
} from 'lib/util/datePickerHelpers';

const postHeightChangedMessage = (height: number) => {
  window.parent.postMessage(
    {
      type: 'HEIGHT_CHANGED',
      height,
    },
    '*'
  );
};

const postOpenGuestSelectModalMessage = (url: string) => {
  window.parent.postMessage(
    {
      type: 'OPEN_GUEST_SELECT_MODAL',
      url,
    },
    '*'
  );
};

export const ProductGroupDateForm = () => {
  const { t } = useTranslation();

  const productSummaries = useSelector((state: ReduxState) => state.server.productSummaries.all);
  const settings = useSelector((state: ReduxState) => state.server.settings.all);
  const productGroupInstances = useSelector(
    (state: ReduxState) => state.productInstances.productGroup
  );
  const productGroup = settings.booking_widget_calendar_product_groups;

  const router = useRouter();

  const [date, setDate] = React.useState('');
  const [showModal, setShowModal] = React.useState(false);
  const [selectedProductGroup, setSelectedProductGroup] = React.useState<
    BookingWidgetCalendarProductGroup
  >(productGroup?.[0] ?? {});
  const [dateFormRef, dateFormBounds] = useBounds<HTMLDivElement>();

  React.useEffect(() => {
    setSelectedProductGroup(productGroup?.[0] ?? {});
  }, [productGroup]);
  React.useEffect(() => {
    postHeightChangedMessage(dateFormBounds.height);
  }, [dateFormBounds.height]);

  const handleProductGroupChange = (event: React.ChangeEvent<HTMLSelectElement>) =>
    setSelectedProductGroup(
      productGroup?.find((g) => g.product_group_name === event.target.value) ?? {}
    );

  const groupProductsSummaries = productSummaries.filter((product) =>
    selectedProductGroup?.product_ids?.includes(product.id)
  );

  const isModal = Boolean(router?.query.isModal);

  const groupAttrMap = productGroupInstances?.map((i) =>
    getAttrByDateMap(
      i?.product_instances ?? [],
      groupProductsSummaries.find((p) => p.id === i.product_id)?.start_timezone ?? '',
      0
    )
  );

  const baseUrl = useUrl(`/productSelect`);

  const handleDateClick = (date: string) => {
    let embeddedUrl = `/embed${baseUrl}`;

    if (embeddedUrl.includes('?')) {
      embeddedUrl = `${embeddedUrl}&date=${date}&productGroup=${
        selectedProductGroup?.product_group_name ?? ''
      }`;
    } else {
      embeddedUrl = `${embeddedUrl}?date=${date}&productGroup=${
        selectedProductGroup?.product_group_name ?? ''
      }`;
    }

    const isDisplayingInIframe =
      typeof window !== 'undefined' && window.location !== window.parent.location;

    if (isModal) {
      setDate(date);
      setShowModal(true);
    } else if (isDisplayingInIframe) {
      postOpenGuestSelectModalMessage(`${window.location.origin}${embeddedUrl}`);
    } else {
      router.replace(embeddedUrl);
    }
  };

  if (productSummaries.length === 0) {
    return null;
  }

  const postCloseModalMessage = () => {
    window.parent.postMessage(
      {
        type: 'CLOSE_MODAL',
      },
      '*'
    );
  };

  const onClose = () => {
    const isDisplayingInIframe =
      typeof window !== 'undefined' && window.location !== window.parent.location;
    if (isDisplayingInIframe) {
      postCloseModalMessage();
    } else {
      router.back();
    }
  };

  const isGroupOnSale = Boolean(
    groupProductsSummaries.some(
      (p) => p?.booking_widget_settings?.is_visible && !p?.booking_widget_settings?.is_view_only
    )
  );

  const hasProductGroupPromotionDate = groupAttrMap?.some((g) => attrMapHasPromotionDate(g));
  const hasProductGroupFullyBookedDate = groupAttrMap?.some((g) => attrMapHasFullyBookedDate(g));

  return (
    <>
      <div className="widget-multi-calendar--frame">
        <div ref={dateFormRef} className="widget-multi-calendar">
          {showModal && <div className={styles['dimmer']} />}
          <div className="widget-multi-calendar__header">
            {t('Make a booking')}
            {isModal && (
              <p className="widget-multi-calendar__header__close" onClick={onClose}>
                <span />
              </p>
            )}
          </div>
          <div className="widget-multi-calendar__body">
            <div className="widget-multi-calendar__body__tour">
              <p className="widget-multi-calendar__body__tour__title">{t('Select Plan Type')}</p>
              <label className="widget-multi-calendar__body__tour__select">
                <select
                  className="widget-multi-calendar__body__tour__select__item"
                  value={selectedProductGroup?.product_group_name ?? ''}
                  onChange={handleProductGroupChange}
                >
                  {productGroup?.map((group, idx) => (
                    <option key={idx} value={group?.product_group_name}>
                      {group?.product_group_name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            {!isGroupOnSale && (
              <div
                className="widget-multi-calendar__body__tour"
                style={{ borderBottom: 'none', backgroundColor: 'white' }}
              >
                <p className="widget-multi-calendar__body__tour__title" style={{ color: 'red' }}>
                  {t('Sorry, this product is currently not available.')}
                </p>
              </div>
            )}
            {isGroupOnSale && (
              <>
                <ProductGroupDatePicker
                  productGroup={selectedProductGroup}
                  onDateClick={handleDateClick}
                />
                <ul className="widget-multi-calendar__body__comment">
                  <li className="widget-multi-calendar__body__comment__item">
                    <p className="widget-multi-calendar__body__comment__item__ic">
                      <InstantIcon />
                    </p>
                    <p className="widget-multi-calendar__body__comment__item__name">
                      {' '}
                      : {t('Instant')}
                    </p>
                    <p className="widget-multi-calendar__body__comment__item__hatena">?</p>
                    <p className="widget-multi-calendar__body__comment__item__info">
                      {t(
                        'Instant bookings will be confirmed automatically within seconds of submission'
                      )}
                    </p>
                  </li>
                  <li className="widget-multi-calendar__body__comment__item">
                    <p className="widget-multi-calendar__body__comment__item__ic">
                      <RequestIcon />
                    </p>
                    <p className="widget-multi-calendar__body__comment__item__name">
                      {' '}
                      : {t('Request')}
                    </p>
                    <p className="widget-multi-calendar__body__comment__item__hatena">?</p>
                    <p className="widget-multi-calendar__body__comment__item__info">
                      {t('Request-only bookings require explicit confirmation from supplier')}
                    </p>
                  </li>
                  {hasProductGroupFullyBookedDate && (
                    <li className="widget-multi-calendar__body__comment__item">
                      <p className="widget-multi-calendar__body__comment__item__ic">
                        <img src="/static/images/ic_fullybooked.svg" alt="closed" />
                      </p>
                      <p className="widget-multi-calendar__body__comment__item__name">
                        {' '}
                        : {t('Fully Booked')}
                      </p>
                    </li>
                  )}
                  {hasProductGroupPromotionDate && (
                    <li className="widget-multi-calendar__body__comment__item">
                      <p
                        className={clsx('widget-multi-calendar__body__comment__item__ic', 'promo')}
                      >
                        <img src="/static/images/ic_promo.svg" />
                      </p>
                      <p className="widget-multi-calendar__body__comment__item__name">
                        {' '}
                        : {t('Campaign')}
                      </p>
                      <p className="widget-multi-calendar__body__comment__item__hatena">?</p>
                      <p className="widget-multi-calendar__body__comment__item__info">
                        {t('One or more campaigns apply to bookings on this date')}
                      </p>
                    </li>
                  )}
                  <li className="widget-multi-calendar__body__comment__item">
                    <p className="widget-multi-calendar__body__comment__item__ic">
                      <img src="/static/images/ic_ng.svg" alt="closed" />
                    </p>
                    <p className="widget-multi-calendar__body__comment__item__name">
                      {' '}
                      : {t('Closed')}
                    </p>
                  </li>
                </ul>
              </>
            )}
          </div>
        </div>
      </div>
      {showModal && (
        <ProductSelectModalView
          productGroup={selectedProductGroup ?? null}
          date={date}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
};
